/**
 * Email Management Service
 * Handles all email-related API calls
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v3';

// Email interfaces
export interface EmailData {
  id: string;
  message_id: string;
  thread_id: string;
  from_address: string;
  to_addresses: string[];
  cc_addresses: string[];
  bcc_addresses: string[];
  reply_to?: string;
  subject: string;
  body_text: string;
  body_html: string;
  direction: 'inbound' | 'outbound';
  email_date: string;
  received_date?: string;
  sent_date?: string;
  mailjet_message_id?: string;
  status: 'read' | 'unread';
  folder: string;
  is_starred: boolean;
  linked_entity_type?: 'order' | 'case' | 'customer' | 'supplier';
  linked_entity_id?: string;
  attachments: EmailAttachment[];
  labels: string[];
  created_at: string;
  updated_at: string;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  file_size: number;
  mime_type: string;
}

export interface EmailFolder {
  name: string;
  count: number;
  isSystem: boolean;
}

export interface EmailLabel {
  name: string;
  color: string;
  count: number;
}

export interface EmailListRequest {
  folder?: string;
  status?: 'read' | 'unread' | 'starred';
  search?: string;
  labels?: string[];
  linkedEntityType?: 'order' | 'case' | 'customer' | 'supplier';
  linkedEntityId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface SendEmailRequest {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  textBody: string;
  htmlBody?: string;
  linkedEntityType?: 'order' | 'case' | 'customer' | 'supplier';
  linkedEntityId?: string;
  labels?: string[];
}

export interface EmailAnalytics {
  totalEmails: number;
  inboundEmails: number;
  outboundEmails: number;
  unreadEmails: number;
  emailsByFolder: Record<string, number>;
  emailsByLabel: Record<string, number>;
  emailsByDay: Array<{ date: string; count: number }>;
}

class EmailService {
  private baseUrl = `${API_BASE_URL}/emails`;

  /**
   * Get list of emails with filtering
   */
  async getEmails(filters: EmailListRequest = {}): Promise<EmailData[]> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await axios.get(`${this.baseUrl}?${params.toString()}`);
      return response.data.data.emails;
    } catch (error) {
      console.error('Error fetching emails:', error);
      throw error;
    }
  }

  /**
   * Get single email by ID
   */
  async getEmail(id: string): Promise<EmailData> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`);
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching email:', error);
      throw error;
    }
  }

  /**
   * Send new email
   */
  async sendEmail(emailData: SendEmailRequest): Promise<string> {
    try {
      const response = await axios.post(this.baseUrl, emailData);
      return response.data.data.id;
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  }

  /**
   * Update email (status, folder, labels)
   */
  async updateEmail(id: string, updates: Partial<EmailData>): Promise<EmailData> {
    try {
      const response = await axios.put(`${this.baseUrl}/${id}`, updates);
      return response.data.data;
    } catch (error) {
      console.error('Error updating email:', error);
      throw error;
    }
  }

  /**
   * Get email folders
   */
  async getFolders(): Promise<{system: EmailFolder[], custom: EmailFolder[]}> {
    try {
      const response = await axios.get(`${this.baseUrl}/folders`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching folders:', error);
      throw error;
    }
  }

  /**
   * Create custom folder
   */
  async createFolder(name: string): Promise<void> {
    try {
      await axios.post(`${this.baseUrl}/folders`, { name });
    } catch (error) {
      console.error('Error creating folder:', error);
      throw error;
    }
  }

  /**
   * Get email labels
   */
  async getLabels(): Promise<EmailLabel[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/labels`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching labels:', error);
      throw error;
    }
  }

  /**
   * Create new label
   */
  async createLabel(name: string, color: string): Promise<void> {
    try {
      await axios.post(`${this.baseUrl}/labels`, { name, color });
    } catch (error) {
      console.error('Error creating label:', error);
      throw error;
    }
  }

  /**
   * Search emails
   */
  async searchEmails(query: string, filters: Omit<EmailListRequest, 'search'> = {}): Promise<EmailData[]> {
    try {
      const params = new URLSearchParams();
      params.append('q', query);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await axios.get(`${this.baseUrl}/search?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error searching emails:', error);
      throw error;
    }
  }

  /**
   * Get email analytics
   */
  async getAnalytics(filters: {
    dateFrom?: string;
    dateTo?: string;
    entityType?: string;
    entityId?: string;
  } = {}): Promise<EmailAnalytics> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${this.baseUrl}/analytics?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }

  /**
   * Get email thread
   */
  async getThread(threadId: string): Promise<EmailData[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/threads/${threadId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching thread:', error);
      throw error;
    }
  }

  /**
   * Link email to order/case
   */
  async linkToEntity(emailId: string, entityType: string, entityId: string): Promise<void> {
    try {
      await axios.post(`${this.baseUrl}/${emailId}/link`, {
        entityType,
        entityId
      });
    } catch (error) {
      console.error('Error linking email:', error);
      throw error;
    }
  }

  /**
   * Get emails for specific order
   */
  async getOrderEmails(orderId: string): Promise<EmailData[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/orders/${orderId}/emails`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching order emails:', error);
      throw error;
    }
  }
}

export const emailService = new EmailService();
export default emailService;
